import type { NwsPeriod } from '@/lib/nws/types'
import { weatherIcon } from '@/lib/nws/weather-icon'
import { WeatherGlyph } from '@/components/WeatherGlyph'
import { fmtWeekday, isToday } from '@/lib/time'
import { tempColor } from './tempRamp'
import { Droplet } from 'lucide-react'

interface Day {
  key: string
  label: string
  isToday: boolean
  hi: number | null
  lo: number | null
  icon: string
  pop: number
}

/** Fold NWS day/night periods into 7 daily rows. */
function foldDays(periods: NwsPeriod[]): Day[] {
  const byDay = new Map<string, Day>()
  for (const p of periods) {
    const dayKey = new Date(p.startTime).toDateString()
    const existing = byDay.get(dayKey) ?? {
      key: dayKey,
      label: isToday(p.startTime) ? 'Today' : fmtWeekday(p.startTime),
      isToday: isToday(p.startTime),
      hi: null,
      lo: null,
      icon: p.shortForecast,
      pop: 0,
    }
    if (p.isDaytime) {
      existing.hi = p.temperature
      existing.icon = p.shortForecast // prefer the daytime condition glyph
    } else {
      existing.lo = p.temperature
    }
    existing.pop = Math.max(existing.pop, p.probabilityOfPrecipitation?.value ?? 0)
    byDay.set(dayKey, existing)
  }
  return [...byDay.values()].slice(0, 7)
}

export function DailyForecast({
  periods,
  currentTemp = null,
  todayHigh = null,
  todayLow = null,
}: {
  periods: NwsPeriod[]
  /** Live temperature, for the "now" marker on Today's bar. */
  currentTemp?: number | null
  /** Realized today hi/lo (so Today's row is never blank in the evening). */
  todayHigh?: number | null
  todayLow?: number | null
}) {
  const days = foldDays(periods)
  // Backfill Today's hi/lo from the realized values so the row shows a full range.
  for (const d of days) {
    if (!d.isToday) continue
    if (todayHigh != null) d.hi = d.hi == null ? todayHigh : Math.max(d.hi, todayHigh)
    if (todayLow != null) d.lo = d.lo == null ? todayLow : Math.min(d.lo, todayLow)
  }
  // ONE shared weekly scale across every day's hi/lo → bars are directly comparable.
  const allTemps = days.flatMap((d) => [d.hi, d.lo].filter((t): t is number => t != null))
  const min = allTemps.length ? Math.min(...allTemps) : 0
  const max = allTemps.length ? Math.max(...allTemps) : 1

  return (
    <div className="daily card">
      {days.map((d, i) => {
        const iconKey = weatherIcon(d.icon, true)
        return (
          <div className="day" key={d.key}>
            <span className="day__name">{d.label}</span>
            <span className="day__pop num">
              {d.pop >= 20 ? (
                <>
                  <Droplet size={11} />
                  {d.pop}%
                </>
              ) : (
                ''
              )}
            </span>
            <WeatherGlyph code={iconKey} size={22} calm className="day__icon" />
            <span className="day__lo num">{d.lo != null ? `${Math.round(d.lo)}°` : '—'}</span>
            <TempBar
              lo={d.lo}
              hi={d.hi}
              min={min}
              max={max}
              index={i}
              now={d.isToday ? currentTemp : null}
            />
            <span className="day__hi num">{d.hi != null ? `${Math.round(d.hi)}°` : '—'}</span>
          </div>
        )
      })}
    </div>
  )
}

/** The day's hi→lo range on the SHARED weekly scale; gradient encodes the temps. */
function TempBar({
  lo,
  hi,
  min,
  max,
  index,
  now,
}: {
  lo: number | null
  hi: number | null
  min: number
  max: number
  index: number
  now: number | null
}) {
  const span = Math.max(max - min, 1)
  // Today's daytime high can already be past (evening) → fall back to the live temp so
  // the bar still reads as a range (low → now) rather than an empty track.
  const effHi = hi ?? now
  const left = lo != null ? ((lo - min) / span) * 100 : 0
  const right = effHi != null ? ((effHi - min) / span) * 100 : 100
  const fill =
    lo != null && effHi != null
      ? `linear-gradient(90deg, ${tempColor(lo)}, ${tempColor((lo + effHi) / 2)}, ${tempColor(effHi)})`
      : 'var(--surface-3)'
  const nowPct =
    now != null ? Math.max(0, Math.min(100, ((now - min) / span) * 100)) : null
  return (
    <span className="day__bar">
      <span
        // keyed so the grow-in replays when the data refreshes
        key={`${lo}-${hi}`}
        className="day__bar-fill"
        style={{ left: `${left}%`, right: `${100 - right}%`, background: fill, animationDelay: `${index * 45}ms` }}
      />
      {nowPct != null && (
        <span className="day__bar-now" style={{ left: `${nowPct}%` }} aria-label="current temperature" />
      )}
    </span>
  )
}
